/**
 * DocumentDropZone Component
 * Universal drag-and-drop document upload for any modal/view
 * Per docstore.yaml specification
 */

import React, { useState, useCallback, useRef } from 'react';
import { Upload, File, X, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { documentAPI } from '../../api.js';
// Source unique des libellés de type de document : docstore.js. Ce composant en avait
// une copie partielle, qui divergeait du mapping de référence (2026-08-23).
import { getDocumentTypeLabel } from '../../config/docstore.js';

// Document types by scope
const DOCUMENT_TYPES_BY_SCOPE = {
  VEHICLE: ['VEHICLE_REGISTRATION', 'VEHICLE_INSURANCE_CARD', 'TECHNICAL_INSPECTION', 'LEASE_CONTRACT', 'PURCHASE_INVOICE', 'MAINTENANCE_INVOICE', 'VEHICLE_PHOTO', 'VEHICLE_MISC'],
  DRIVER: ['DRIVING_LICENCE', 'IDENTITY_DOCUMENT', 'DRIVER_CERTIFICATION', 'MEDICAL_CERTIFICATE', 'DRIVER_SANCTION', 'DRIVER_PHOTO'],
  CONTRACT: ['FLEET_SCHEDULE', 'SPECIAL_CONDITIONS', 'GENERAL_CONDITIONS', 'CONTRACT_AMENDMENT', 'INSURANCE_CERTIFICATE', 'INSURANCE_CARD', 'INSURANCE_ATTESTATION'],
  CLAIM: ['ACCIDENT_DECLARATION', 'CLAIM_PHOTOS', 'DRIVER_STATEMENT', 'EXPERT_REPORT', 'REPAIR_INVOICE', 'REPAIR_QUOTE', 'CLAIM_CORRESPONDENCE', 'SETTLEMENT_DOCUMENT'],
  ADMIN: ['TOOL_INVOICE', 'TOOL_MANUAL', 'GENERAL_INVOICE', 'GENERAL_QUOTE'],
};

// Allowed file extensions
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.doc', '.docx', '.xls', '.xlsx'];

// Max file size (50MB)
const MAX_FILE_SIZE = 50 * 1024 * 1024;

/**
 * DocumentDropZone Component
 *
 * @param {Object} props
 * @param {string} props.scope - VEHICLE, DRIVER, CONTRACT, CLAIM, ADMIN
 * @param {string} props.referenceId - Entity ID (e.g., VHC_000123, DRV_000045)
 * @param {string[]} [props.allowedTypes] - Allowed document types (optional filter)
 * @param {number} [props.maxFiles=10] - Maximum files per upload
 * @param {Function} [props.onUploadComplete] - Callback when upload completes
 * @param {Function} [props.onError] - Callback on error
 * @param {boolean} [props.compact=false] - Compact mode for inline display
 */
export default function DocumentDropZone({
  scope,
  referenceId,
  allowedTypes,
  maxFiles = 10,
  onUploadComplete,
  onError,
  compact = false
}) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [validTo, setValidTo] = useState('');
  const fileInputRef = useRef(null);

  // Get available document types for this scope
  const availableTypes = allowedTypes || DOCUMENT_TYPES_BY_SCOPE[scope] || [];

  // Validate file
  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { valid: false, error: t('common.docs.dropzone.extensionNotAllowed', { ext }) };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t('common.docs.dropzone.fileTooLarge') };
    }
    return { valid: true };
  };

  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle file input change
  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    e.target.value = ''; // Reset input
  };

  // Process files
  const handleFiles = (newFiles) => {
    if (files.length + newFiles.length > maxFiles) {
      onError?.({ message: t('common.docs.dropzone.maxFilesExceeded', { count: maxFiles }) });
      return;
    }

    const processedFiles = newFiles.map(file => {
      const validation = validateFile(file);
      return {
        file,
        name: file.name,
        size: file.size,
        valid: validation.valid,
        error: validation.error,
        status: 'pending', // pending, uploading, success, error
        progress: 0
      };
    });

    setFiles(prev => [...prev, ...processedFiles]);
  };

  // Remove file from list
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload all files
  const handleUpload = async () => {
    if (!selectedType) {
      onError?.({ message: t('documents.dropzone.selectTypeFirst') });
      return;
    }

    const validFiles = files.filter(f => f.valid && f.status !== 'success');
    if (validFiles.length === 0) return;

    setUploading(true);

    const results = [];
    for (let i = 0; i < validFiles.length; i++) {
      const fileItem = validFiles[i];

      // Update status to uploading
      setFiles(prev => prev.map((f, idx) =>
        f.name === fileItem.name ? { ...f, status: 'uploading' } : f
      ));

      try {
        const result = await documentAPI.upload(fileItem.file, {
          scope,
          reference_id: referenceId,
          document_type: selectedType,
          valid_to: validTo || null,
        });

        if (result.ok) {
          setFiles(prev => prev.map((f) =>
            f.name === fileItem.name ? { ...f, status: 'success', document: result.data } : f
          ));
          results.push({ success: true, document: result.data });
        } else {
          setFiles(prev => prev.map((f) =>
            f.name === fileItem.name ? { ...f, status: 'error', error: result.error?.message } : f
          ));
          results.push({ success: false, error: result.error?.message });
        }
      } catch (err) {
        setFiles(prev => prev.map((f) =>
          f.name === fileItem.name ? { ...f, status: 'error', error: err.message } : f
        ));
        results.push({ success: false, error: err.message });
      }
    }

    setUploading(false);

    // Notify parent
    const successCount = results.filter(r => r.success).length;
    if (successCount > 0) {
      onUploadComplete?.(results.filter(r => r.success).map(r => r.document));
    }
  };

  // Clear all files
  const clearFiles = () => {
    setFiles([]);
    setSelectedType('');
    setValidTo('');
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get status icon
  const StatusIcon = ({ status }) => {
    switch (status) {
      case 'uploading':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  if (compact && files.length === 0) {
    return (
      <div
        onClick={() => fileInputRef.current?.click()}
        className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
      >
        <Upload className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">{t('documents.dropzone.addDocument')}</span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
        `}
      >
        <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
        <p className="text-sm text-gray-600 mb-1">
          {isDragging ? t('documents.dropzone.dropHere') : t('documents.dropzone.dragHere')}
        </p>
        <p className="text-xs text-gray-400">
          {t('documents.dropzone.browseHint', { count: maxFiles })}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {t('documents.dropzone.acceptedFormats')}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-4">
          {/* Document Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.docs.dropzone.typeLabel')}
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t('documents.dropzone.selectTypeOption')}</option>
              {availableTypes.map(type => (
                <option key={type} value={type}>
                  {getDocumentTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>

          {/* Files */}
          <div className="border rounded-lg divide-y">
            {files.map((fileItem, index) => (
              <div key={index} className="flex items-center gap-3 p-3">
                <File className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{fileItem.name}</p>
                  <p className="text-xs text-gray-500">
                    {formatSize(fileItem.size)}
                    {fileItem.error && (
                      <span className="text-red-500 ml-2">{fileItem.error}</span>
                    )}
                  </p>
                </div>
                <StatusIcon status={fileItem.status} />
                {fileItem.status !== 'uploading' && fileItem.status !== 'success' && (
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={clearFiles}
              disabled={uploading}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading || !selectedType || files.every(f => !f.valid || f.status === 'success')}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
              {uploading ? t('documents.dropzone.uploading') : t('documents.dropzone.submit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
